import { useNavigate } from "react-router-dom";
import { Radar, ClipboardList, HardHat, Users, IndianRupee, FolderArchive, ArrowRight, Radio } from "lucide-react";

export default function LandingPage() {
  const navigate = useNavigate();
  const token = localStorage.getItem("contractor_os_token");

  return (
    <div className="min-h-screen bg-[#0A0A08] text-[#F5F2E8]" data-testid="landing-page">
      {/* NAV */}
      <nav className="flex justify-between items-center px-6 md:px-12 py-5 border-b border-[#27272A] sticky top-0 bg-[#0A0A08]/92 backdrop-blur-xl z-50">
        <div className="font-heading text-2xl tracking-[3px]" data-testid="logo">
          CONTRACTOR<span className="text-[#FF5C1A]">OS</span>
        </div>
        <ul className="hidden md:flex gap-8 list-none">
          <li><a href="#modules" className="text-[#888880] text-xs font-medium tracking-widest uppercase hover:text-[#F5F2E8] transition-colors">Product</a></li>
          <li><a href="#pricing" className="text-[#888880] text-xs font-medium tracking-widest uppercase hover:text-[#F5F2E8] transition-colors">Pricing</a></li>
          <li><a href="#pain" className="text-[#888880] text-xs font-medium tracking-widest uppercase hover:text-[#F5F2E8] transition-colors">Why Now</a></li>
        </ul>
        <button
          data-testid="nav-cta-button"
          onClick={() => navigate(token ? "/app/dashboard" : "/auth")}
          className="bg-[#FF5C1A] text-[#0A0A08] px-6 py-2.5 font-mono text-xs font-bold tracking-widest uppercase hover:bg-[#FFB800] transition-colors"
        >
          {token ? "Go to App" : "Start Free"}
        </button>
      </nav>

      {/* HERO */}
      <section className="px-6 md:px-12 pt-20 md:pt-28 pb-16 max-w-7xl mx-auto relative">
        <div className="inline-flex items-center gap-2 bg-[#FF5C1A]/12 border border-[#FF5C1A]/30 px-4 py-1.5 font-mono text-[11px] text-[#FF5C1A] tracking-[2px] uppercase mb-8 animate-fade-up">
          <span className="w-1.5 h-1.5 bg-[#FF5C1A] rounded-full animate-pulse-dot" />
          India's First Contractor Command Center
        </div>

        <h1 className="font-heading text-6xl sm:text-7xl md:text-8xl lg:text-[120px] leading-[0.92] tracking-wide mb-8 animate-fade-up delay-100">
          RUN YOUR<br />
          <span className="text-[#FF5C1A]">CONTRACTING</span><br />
          <span className="text-transparent" style={{ WebkitTextStroke: '2px #F5F2E8' }}>BUSINESS</span><br />
          LIKE A PRO
        </h1>

        <p className="text-lg text-[#888880] max-w-xl leading-relaxed mb-12 font-light animate-fade-up delay-200">
          Tenders, bids, labour, finance, documents — <strong className="text-[#F5F2E8] font-medium">one system.</strong> Built for India's <span className="font-mono text-[#FFB800]">50L-10Cr</span> contractors who are done running businesses on WhatsApp.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 items-start animate-fade-up delay-300">
          <button
            data-testid="hero-cta-button"
            onClick={() => navigate(token ? "/app/dashboard" : "/auth")}
            className="bg-[#FF5C1A] text-[#0A0A08] px-10 py-4 font-mono text-sm font-bold tracking-widest uppercase hover:bg-[#FFB800] hover:-translate-y-0.5 transition-all hover:shadow-[0_12px_40px_rgba(255,92,26,0.3)]"
          >
            Get Early Access
          </button>
          <a href="#modules" className="text-[#888880] font-mono text-xs font-bold tracking-widest uppercase flex items-center gap-2 py-4 hover:text-[#F5F2E8] transition-colors">
            See How It Works <ArrowRight size={16} />
          </a>
        </div>

        {/* Hero Visual - Desktop only */}
        <div className="hidden lg:block absolute right-12 top-24 w-[400px] bg-[#1A1A18] border border-[#27272A] p-6 animate-fade-up delay-400">
          <div className="flex justify-between items-center mb-5">
            <span className="font-mono text-[11px] text-[#888880] tracking-[2px] uppercase">Live Dashboard</span>
            <span className="flex items-center gap-1.5 font-mono text-[10px] text-[#2ECC71]">
              <span className="w-1.5 h-1.5 bg-[#2ECC71] rounded-full animate-pulse-dot" />
              3 New Tenders
            </span>
          </div>
          <div className="grid grid-cols-3 gap-2 mb-5">
            {[
              { val: "2.4Cr", label: "Active Bids" },
              { val: "47", label: "Workers" },
              { val: "6", label: "Projects" }
            ].map((m) => (
              <div key={m.label} className="bg-[#0A0A08] p-3 text-center">
                <div className="font-heading text-2xl text-[#FF5C1A] tracking-wide">{m.val}</div>
                <div className="font-mono text-[10px] text-[#888880] tracking-widest uppercase">{m.label}</div>
              </div>
            ))}
          </div>
          <div className="flex flex-col gap-1.5">
            {[
              { name: "NHAI Road Widening — UP", val: "84L", status: "NEW", cls: "bg-[#2ECC71]/15 text-[#2ECC71]" },
              { name: "Municipal Drainage — Bihar", val: "1.2Cr", status: "BIDDING", cls: "bg-[#FFB800]/15 text-[#FFB800]" },
              { name: "School Renovation — MP", val: "38L", status: "WON", cls: "bg-[#FF5C1A]/15 text-[#FF5C1A]" },
            ].map((t) => (
              <div key={t.name} className="bg-[#0A0A08] px-3 py-2.5 flex justify-between items-center">
                <div>
                  <div className="text-xs text-[#F5F2E8] font-medium">{t.name}</div>
                  <div className="font-mono text-[11px] text-[#FFB800]">{t.val}</div>
                </div>
                <span className={`text-[10px] px-2 py-0.5 font-mono ${t.cls}`}>{t.status}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STATS BAR */}
      <div className="bg-[#1A1A18] border-y border-[#27272A] py-8 px-6 flex flex-wrap justify-center gap-12 md:gap-20">
        {[
          { num: "5L+", label: "Active Contractors" },
          { num: "80%", label: "Using WhatsApp Only" },
          { num: "42L Cr", label: "Annual Govt Contracts" },
          { num: "0", label: "Platforms For Them" },
        ].map((s) => (
          <div key={s.label} className="text-center">
            <div className="font-heading text-4xl md:text-5xl text-[#FF5C1A] tracking-wide">{s.num}</div>
            <div className="font-mono text-[11px] text-[#888880] tracking-[2px] uppercase mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      {/* MODULES */}
      <section id="modules" className="px-6 md:px-12 py-24 max-w-7xl mx-auto">
        <div className="font-mono text-[11px] text-[#FF5C1A] tracking-[3px] uppercase mb-4">The Platform</div>
        <div className="font-heading text-4xl sm:text-5xl md:text-6xl tracking-wide leading-none mb-16">
          EVERYTHING A<br />CONTRACTOR NEEDS
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[2px] bg-[#27272A]/40">
          {[
            { icon: <Radar size={28} />, num: "01", title: "Tender Radar", desc: "Auto-aggregates tenders from GeM, CPWD, NIC, and 20+ state portals. Instant alerts matching your category and location.", tag: "Never Miss a Bid" },
            { icon: <ClipboardList size={28} />, num: "02", title: "Bid Engine", desc: "Document checklist, bid price calculator, submission deadline tracker. Know exactly what to submit and when.", tag: "Win More Contracts" },
            { icon: <HardHat size={28} />, num: "03", title: "Project HQ", desc: "Milestones, daily site progress, photo logs. Full visibility from office to site — no surprises.", tag: "Zero Surprises" },
            { icon: <Users size={28} />, num: "04", title: "Labour Manager", desc: "Daily attendance, wages, advance tracking, headcount per site. Stop labour leakage. Pay on time.", tag: "Stop Leakage" },
            { icon: <IndianRupee size={28} />, num: "05", title: "Finance Tracker", desc: "Cash in/out per project, pending payment reminders, profit per project. Know if you're making money.", tag: "Know Your Numbers" },
            { icon: <FolderArchive size={28} />, num: "06", title: "Document Vault", desc: "GST, PAN, RC, trade licenses — all stored, tagged, expiry-tracked. One link to share during a bid.", tag: "Always Ready" },
          ].map((m) => (
            <div key={m.num} className="bg-[#111110] p-8 md:p-10 relative overflow-hidden group hover:bg-[#1A1A18] hover:-translate-y-0.5 transition-all cursor-pointer border-t-2 border-t-transparent hover:border-t-[#FF5C1A]">
              <div className="font-heading text-6xl text-white/[0.04] absolute top-4 right-6 tracking-wide">{m.num}</div>
              <div className="text-[#FF5C1A] mb-5">{m.icon}</div>
              <div className="font-heading text-2xl tracking-[2px] mb-3">{m.title}</div>
              <p className="text-sm text-[#888880] leading-relaxed font-light">{m.desc}</p>
              <span className="inline-block mt-5 px-3 py-1 bg-[#FF5C1A]/10 border border-[#FF5C1A]/20 font-mono text-[10px] text-[#FF5C1A] tracking-widest uppercase">{m.tag}</span>
            </div>
          ))}
        </div>
      </section>

      {/* PAIN POINTS */}
      <section id="pain" className="py-20 px-6 md:px-12 bg-[#1A1A18] border-t border-[#27272A]">
        <div className="max-w-7xl mx-auto">
          <div className="font-mono text-[11px] text-[#FF5C1A] tracking-[3px] uppercase mb-4">Why Now</div>
          <div className="font-heading text-4xl sm:text-5xl md:text-6xl tracking-wide leading-none mb-12">
            THE PROBLEMS<br />KILLING CONTRACTORS
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-[2px] bg-[#27272A]/40">
            {[
              { icon: <Radio size={24} />, title: "Running on WhatsApp & Calls", desc: "Quotations in voice notes. Labour attendance in chat groups. Multi-crore businesses run like a college project." },
              { icon: <FolderArchive size={24} />, title: "Documents Always Scattered", desc: "Missing experience certificates. Expired trade licenses. Losing 20L bids because one document is missing." },
              { icon: <IndianRupee size={24} />, title: "No Idea About Project Profit", desc: "Material, labour, delays — costs blur together. Did you make money on that road job? Nobody knows." },
              { icon: <Radar size={24} />, title: "Missing Tenders Daily", desc: "Tenders go live on 7 different govt portals. Contractors find out from middlemen — who take a 5% cut." },
            ].map((p) => (
              <div key={p.title} className="bg-[#111110] p-8 flex gap-5 items-start">
                <div className="text-[#FF5C1A] flex-shrink-0 mt-1">{p.icon}</div>
                <div>
                  <div className="font-semibold text-base mb-2">{p.title}</div>
                  <p className="text-sm text-[#888880] leading-relaxed">{p.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="px-6 md:px-12 py-24 max-w-7xl mx-auto">
        <div className="font-mono text-[11px] text-[#FF5C1A] tracking-[3px] uppercase mb-4">Simple Pricing</div>
        <div className="font-heading text-4xl sm:text-5xl md:text-6xl tracking-wide leading-none mb-16">
          PLANS BUILT FOR<br />REAL CONTRACTORS
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-[2px] bg-[#27272A]/40">
          {[
            { name: "Starter", price: "1,999", desc: "For solo contractors getting organized.", featured: false, features: ["Tender Radar (10 alerts/day)", "Document Vault (5GB)", "2 Active Projects", "Basic Finance Tracker", "WhatsApp Alerts"] },
            { name: "Professional", price: "3,999", desc: "For active contractors managing multiple projects.", featured: true, features: ["Unlimited Tender Alerts", "Document Vault (50GB)", "10 Active Projects", "Full Finance Tracker", "Labour Manager (50 workers)", "Bid Engine + Checklist"] },
            { name: "Enterprise", price: "7,999", desc: "For large contractors with 100+ workers.", featured: false, features: ["Everything in Pro", "Unlimited Workers", "Unlimited Projects", "CA/Accountant Access", "Priority WhatsApp Support", "Custom Reports & Export"] },
          ].map((plan) => (
            <div key={plan.name} className={`p-10 md:p-12 relative ${plan.featured ? 'bg-[#FF5C1A]' : 'bg-[#111110]'}`}>
              {plan.featured && (
                <div className="absolute -top-px right-8 bg-[#FFB800] text-[#0A0A08] font-mono text-[10px] font-bold tracking-widest px-3 py-1.5 uppercase">Most Popular</div>
              )}
              <div className={`font-mono text-[11px] tracking-[3px] uppercase mb-2 ${plan.featured ? 'text-[#0A0A08]/60' : 'text-[#888880]'}`}>{plan.name}</div>
              <div className={`font-heading text-6xl tracking-wide leading-none mb-1 ${plan.featured ? 'text-[#0A0A08]' : ''}`}>{plan.price}</div>
              <div className={`font-mono text-xs mb-8 ${plan.featured ? 'text-[#0A0A08]/60' : 'text-[#888880]'}`}>/ month</div>
              <p className={`text-sm leading-relaxed mb-8 pb-8 border-b ${plan.featured ? 'text-[#0A0A08] border-[#0A0A08]/15' : 'text-[#888880] border-[#27272A]'}`}>{plan.desc}</p>
              <ul className="flex flex-col gap-3.5 mb-8">
                {plan.features.map((f) => (
                  <li key={f} className={`text-sm flex items-center gap-2.5 ${plan.featured ? 'text-[#0A0A08]' : 'text-[#888880]'}`}>
                    <span className={`w-1.5 h-1.5 flex-shrink-0 ${plan.featured ? 'bg-[#0A0A08]' : 'bg-[#FF5C1A]'}`} />
                    {f}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => navigate("/auth")}
                className={`w-full py-3.5 font-mono text-xs font-bold tracking-widest uppercase transition-colors ${
                  plan.featured
                    ? 'bg-[#0A0A08] text-[#F5F2E8] hover:bg-[#1A1A18]'
                    : 'bg-transparent border border-[#27272A] text-[#F5F2E8] hover:border-[#FF5C1A] hover:text-[#FF5C1A]'
                }`}
              >
                Get Started
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* FOOTER */}
      <footer className="px-6 md:px-12 py-12 border-t border-[#27272A] max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
        <div className="font-heading text-xl tracking-[3px]">CONTRACTOR<span className="text-[#FF5C1A]">OS</span></div>
        <div className="font-mono text-[11px] text-[#888880] tracking-widest">Built for India's builders. 2025 Contractor OS</div>
      </footer>
    </div>
  );
}
